// @flow
import * as React from 'react';
import { DragSource, DropTarget } from 'react-dnd';
import { findDOMNode } from 'react-dom';
import { compose } from 'redux';

export const DragItemTypes = {
  Pin: 'pin',
  Board: 'board',
  Section: 'section',
  CarouselItem: 'carouselitem',
};

export type HoverRelativePosition = {
  positionRelativeToCenter: {
    x: number,
    y: number,
  },
  boundingBox: {
    width: number,
    height: number,
  },
};

type Props = {
  children: React.Node,
  connectDragSource: Function,
  connectDropTarget: Function,
  index: number,
  // TODO(yansun): figure out why <Masonry> doesn't play nice with sourceMonitor.isDragging()
  //   for <PinRep> inside <Masonry> monitor.isDragging() is not a reliable source of truth
  //   for drag state, so in that case the parent flags whether there's a drag in progress
  isDragging?: boolean,
  isDraggingInternal: boolean,
  onDragBegin: (index: number) => void,
  onDragEnd: (didDrop: boolean) => void,
  onDragHover: (index: number, relativePosition: ?HoverRelativePosition) => void,
  type: $Values<typeof DragItemTypes>,
};

const itemSource = {
  beginDrag: ({ index, onDragBegin }: Props) => {
    onDragBegin(index);
    return { index };
  },

  endDrag: ({ onDragEnd }: Props, monitor) => onDragEnd(monitor.didDrop()),
};

function getHoverRelativePosition(monitor, component): ?HoverRelativePosition {
  const node = findDOMNode(component);
  if (!(node && node instanceof HTMLElement)) {
    return null;
  }
  const { x, y } = monitor.getClientOffset();
  const { left, right, top, bottom, width, height } = node.getBoundingClientRect();
  const centerX = (left + right) / 2;
  const centerY = (top + bottom) / 2;
  return {
    positionRelativeToCenter: {
      x: x - centerX,
      y: y - centerY,
    },
    boundingBox: {
      width,
      height,
    },
  };
}

const itemTarget = {
  hover: ({ index, onDragHover }: Props, monitor, component) => {
    onDragHover(index, getHoverRelativePosition(monitor, component));
  },
};

const dropCollect = (connect) => ({ connectDropTarget: connect.dropTarget() });

const dragSource = (connect, monitor) => ({
  connectDragSource: connect.dragSource(),
  isDraggingInternal: monitor.isDragging(),
});

function DraggableItem(props: Props) {
  const { children, connectDragSource, connectDropTarget, isDragging, isDraggingInternal } = props;
  const opacity = isDragging || isDraggingInternal ? 0 : 1;
  return connectDragSource(connectDropTarget(<div style={{ opacity }}>{children}</div>));
}

export default compose(
  DropTarget((props) => props.type, itemTarget, dropCollect),
  DragSource((props) => props.type, itemSource, dragSource),
)(DraggableItem);
